import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  clearLocation,
  getGeocodedFilters,
  getMyLocation,
  loadStoredLocation,
  storeLocation,
} from "@/lib/geo";
import type { GeoPoint, GeocodedFilters } from "@/lib/geo";

const STORAGE_KEY = "user_location";
const LOCATION_EVENT = "user-location-changed";

const broadcast = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(LOCATION_EVENT));
  }
};

interface RequestLocationResult {
  location: GeoPoint;
  match: GeocodedFilters | null;
}

interface UseUserLocationReturn {
  userLocation: GeoPoint | null;
  loading: boolean;
  error: string;
  requestLocation: (opts?: { withGeocode?: boolean }) => Promise<RequestLocationResult>;
  clear: () => void;
}

/**
 * Reactive user-geolocation hook.
 * - `userLocation` reflects the current cached coordinates ({lat, lng}) or null.
 * - `requestLocation()` prompts the browser, persists, and (optionally) returns
 *   geocoded filter hints (state/city/neighborhood) when `withGeocode: true`.
 * - `clear()` forgets the stored location.
 * - State stays in sync across components and tabs via storage + custom events.
 */
export default function useUserLocation(): UseUserLocationReturn {
  const [userLocation, setUserLocation] = useState<GeoPoint | null>(loadStoredLocation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const sync = () => setUserLocation(loadStoredLocation());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) sync();
    };
    window.addEventListener(LOCATION_EVENT, sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(LOCATION_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const requestLocation = useCallback(
    async ({ withGeocode = false } = {}): Promise<RequestLocationResult> => {
      setLoading(true);
      setError("");
      try {
        const loc = await getMyLocation();
        storeLocation(loc);
        setUserLocation(loc);
        broadcast();
        if (withGeocode) {
          const match = await getGeocodedFilters(loc.lat, loc.lng, api);
          return { location: loc, match };
        }
        return { location: loc, match: null };
      } catch (e: unknown) {
        const err = e as { code?: number; message?: string };
        const code = err.code;
        const msg =
          code === 1
            ? "Permissão de localização negada. Habilite no navegador para encontrar acompanhantes perto de você."
            : code === 3
              ? "Tempo esgotado ao obter sua localização. Tente novamente."
              : code === 0
                ? "Localização indisponível no momento. Verifique os serviços de localização e tente novamente."
                : err.message || "Não foi possível obter sua localização.";
        setError(msg);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clear = useCallback(() => {
    clearLocation();
    setUserLocation(null);
    setError("");
    broadcast();
  }, []);

  return { userLocation, loading, error, requestLocation, clear };
}